<template>
  <div class="file-tree-container">
    <div class="file-tree-content">
      <div v-if="loading" class="tree-loading">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Carregando...</span>
        </div>
      </div>
      <div v-else-if="error" class="tree-error">
        {{ error }}
      </div>
      <div v-else class="tree-view-content">
        <ul class="tree-root">
          <li v-for="(item, index) in treeItemsToRender" :key="`tree-root-${index}`" class="tree-item">
            <tree-node
              :item="item"
              :level="0"
              :is-shared-context="isSharedContext"
              :current-path="currentPath"
              :file-explorer-service="fileExplorerService"
              :drive-public-service="drivePublicService"
              @node-click="handleNodeClick"
            />
          </li>
        </ul>
        <div v-if="isTreeItemsEmpty" class="tree-empty">
          <span>{{emptyMessage}}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TreeNode from './TreeNode.vue';

export default {
  name: 'FileTree',
  components: {
    TreeNode
  },
  props: {
    rootPath: {
      type: String,
      default: ''
    },
    fileExplorerService: {
      type: Object,
      required: true
    },
    drivePublicService: {
      type: Object,
      default: null
    },
    currentPath: {
      type: String,
      default: ''
    },
    isSharedContext: {
      type: Boolean,
      default: false
    },
    // NOVO: permite passar itens diretamente para a árvore
    treeItems: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      treeItemsInternal: [],
      loading: false,
      error: null
    };
  },
  computed: {
    isDrivePublic() {
      return this.drivePublicService?.isPublic ?? false;
    },
    // Usar treeItems da prop se fornecido, senão usar o interno
    treeItemsToRender() {
      return this.treeItems !== null ? this.treeItems : this.treeItemsInternal;
    },
    isTreeItemsEmpty() {
      return this.treeItemsToRender.length === 0;
    },
    emptyMessage() {
      return this.isSharedContext ? 'Nenhum item compartilhado' : 'Nenhum drive/pasta encontrada';
    }
  },
  mounted() {
    // Só carrega do serviço se não recebeu treeItems por prop
    if (this.treeItems === null) {
      this.loadRootItems();
    }
  },
  methods: {
    async loadRootItems() {
      this.loading = true;
      this.error = null;

      try {
        let rootItems = [];
        if (this.isDrivePublic) {
          const response = await this.drivePublicService.getResources(this.currentPath, this.currentResourceId, this.currentParentId, true);
          rootItems = Array.isArray(response.items) ? response.items : [response.items];
        }else{
          // Carregamos apenas os itens do nível raiz
          // Para o nível raiz, geralmente não temos um resource_id específico
          rootItems = await this.fileExplorerService.getDirectoryChildren('', null, this.isSharedContext);
        }

        // Filtramos apenas pastas e drives para a TreeView
        this.treeItemsInternal = rootItems.filter(item =>
          item.type === 'directory' || item.type === 'drive'
        );
      } catch (err) {
        console.error('Error loading root items:', err);
        this.error = 'Falha ao carregar os drives/itens raiz';
      } finally {
        this.loading = false;
      }
    },
    handleNodeClick(item) {
      // Emitir evento para navegação para a pasta selecionada
      this.$emit('navigate', item);
    },

    /**
     * Atualiza uma subárvore específica após uma alteração (como exclusão de pasta)
     * @param {number} parentId ID do recurso pai que deve ser atualizado
     */
    async refreshSubTree(parentId) {
      console.log('FileTree: Atualizando subárvore com parentId:', parentId);

      try {
        // Se não temos parentId, atualizamos a raiz
        if (!parentId) {
          console.log('ID nulo, atualizando itens raiz');
          await this.loadRootItems();
          return;
        }

        // Se temos o parentId, atualizamos apenas este nó e seus filhos
        console.log('Atualizando apenas o nó com ID:', parentId);
        await this.refreshNodeById(parentId);

        // Emitir evento informando que a árvore foi atualizada
        this.$emit('tree-updated', { parentId });
      } catch (err) {
        console.error('Erro ao atualizar a subárvore:', err);
      }
    },

    /**
     * Atualiza um nó específico na árvore usando seu ID
     * @param {number} nodeId ID do nó a ser atualizado
     */
    async refreshNodeById(nodeId) {
      console.log('Iniciando refreshNodeById para ID:', nodeId);

      // Verificar nó raiz
      if (!nodeId) {
        console.log('ID nulo, atualizando itens raiz');
        await this.loadRootItems();
        return;
      }

      try {
        // Buscar os itens raiz atualizados
        const rootItems = await this.fileExplorerService.getDirectoryChildren('', null, this.isSharedContext);
        const filteredRootItems = rootItems.filter(item =>
          item.type === 'directory' || item.type === 'drive'
        );

        // Atualizar os itens raiz mantendo a estrutura da árvore
        this.treeItemsInternal = filteredRootItems;

        // Emitir eventos para atualizar contadores e estrutura
        this.$nextTick(() => {
          // 1. Notificar via event bus global que a estrutura foi atualizada
          this.$root.$emit('tree-structure-updated', {
            updatedNodeId: nodeId,
            action: 'refresh'
          });
          console.log('Emitido evento global tree-structure-updated para o nó ID:', nodeId);

          // 2. Notificar que os contadores devem ser atualizados
          this.$root.$emit('refresh-tree-counters', {
            type: 'refresh',
            targetNodeId: nodeId
          });

          // 3. Emitir evento local para qualquer ouvinte direto
          this.$emit('tree-updated', { nodeId });
        });
      } catch (error) {
        console.error('Erro durante a atualização do nó:', error);
        // Em caso de erro, ainda atualizamos os itens raiz
        try {
          const rootItems = await this.fileExplorerService.getDirectoryChildren('', null, this.isSharedContext);
          this.treeItemsInternal = rootItems.filter(item =>
            item.type === 'directory' || item.type === 'drive'
          );
        } catch (fallbackError) {
          console.error('Erro crítico ao tentar atualizar itens raiz:', fallbackError);
        }
      }
    }
  }
}
</script>
